import { useCallback, useState } from "react";
import { uploadSourcePdf } from "@/api/lecture";
import { savePdf } from "./lecture/lecture-pdf-store";
import { extractPdfText } from "./lecture/extract-pdf-text";
import { snapshotFile } from "./snapshot-file";

// Shared PDF intake for the Create flow. When a learner picks a PDF in ANY
// framework's start form (not just `lecture`), we DON'T dump its text into the
// source field. Instead we:
//   1. stash the original PDF blob in IndexedDB under a ref (so the marking
//      workspace can render it),
//   2. upload it to the server in the background (R2) for the Library download
//      + native-PDF grounding of the generation calls (Phase C),
//   3. best-effort extract its text for grounding (used by the discussion +
//      per-topic compaction; NEVER shown as editable source material).
// The caller then offers the three-way round chooser (study / mark / generate).
export interface PdfIntake {
  fileName: string;
  pdfRef: string | null;
  /** Resolves once the background R2 upload completes (or null on failure). */
  sourcePdfKey: string | null;
  sourceText: string;
  chars: number;
  processing: boolean;
  note: string;
  error: string | null;
}

const EMPTY: PdfIntake = {
  fileName: "",
  pdfRef: null,
  sourcePdfKey: null,
  sourceText: "",
  chars: 0,
  processing: false,
  note: "",
  error: null,
};

export function usePdfIntake() {
  const [intake, setIntake] = useState<PdfIntake>(EMPTY);

  const reset = useCallback(() => setIntake(EMPTY), []);

  const pick = useCallback(async (f: File) => {
    setIntake({ ...EMPTY, fileName: f.name, processing: true });
    try {
      // Snapshot the bytes once up front (before the <input> resets) so the
      // three consumers below all read a stable in-memory copy — fixes the
      // intermittent empty-read that broke PDF upload / marking start.
      const stable = await snapshotFile(f);
      const ref = await savePdf(stable);
      setIntake((s) => ({ ...s, pdfRef: ref }));
      // Non-blocking R2 upload — failure just means no source PDF for the
      // Library/grounding; never blocks marking or generation.
      void uploadSourcePdf(stable)
        .then((r) => setIntake((s) => ({ ...s, sourcePdfKey: r.key })))
        .catch(() => setIntake((s) => ({ ...s, sourcePdfKey: null })));
      const text = await extractPdfText(stable, (m) => setIntake((s) => ({ ...s, note: m })));
      setIntake((s) => ({
        ...s,
        sourceText: text,
        chars: text.length,
        processing: false,
        note: "",
      }));
    } catch (e) {
      setIntake((s) => ({
        ...s,
        processing: false,
        note: "",
        pdfRef: null,
        error: e instanceof Error ? e.message : "Couldn't read that PDF.",
      }));
    }
  }, []);

  return { intake, pick, reset };
}
